'use client';

import Link from 'next/link';
import type React from 'react';
import { use } from 'react';
import { ThemeSwitcherWrapper } from '@/app/_components/ThemeSwitcherWrapper';
import { useScrollContainer } from '@/app/_lib/providers/ScrollProvider';
import LogoSvg from './LogoSvg';

interface FooterProps {
  yearPromise: Promise<number>;
}

export function Footer({ yearPromise }: FooterProps) {
  const { scrollContainerRef } = useScrollContainer();

  // Use React.use() to unwrap the promise in the client component
  const currYear = use(yearPromise);

  // Use the public environment variables directly in client components
  const appName = process.env.NEXT_PUBLIC_APP_NAME || '';
  const appLegalName = process.env.NEXT_PUBLIC_APP_LEGAL_NAME || '';
  const facilityStreet = process.env.NEXT_PUBLIC_FACILITY_STREET || '';
  const facilityCity = process.env.NEXT_PUBLIC_FACILITY_CITY || '';
  const facilityState = process.env.NEXT_PUBLIC_FACILITY_STATE || '';
  const facilityZipcode = process.env.NEXT_PUBLIC_FACILITY_ZIPCODE || '';
  const facilityLicense = process.env.NEXT_PUBLIC_FACILITY_LICENSE || '';
  const facilityTelephone = process.env.NEXT_PUBLIC_FACILITY_TELEPHONE || '';
  const shortDescription = process.env.NEXT_PUBLIC_APP_SHORT_DESCRIPTION || '';
  const instagramUrl = process.env.NEXT_PUBLIC_INSTAGRAM_URL || '';
  const formattedPhone = facilityTelephone.replace(/[^0-9]/g, '');

  // Validate Instagram URL
  const isValidInstagramUrl = (url: string) => {
    if (!url) return false;
    try {
      const urlObj = new URL(url);
      return urlObj.hostname === 'instagram.com' || urlObj.hostname === 'www.instagram.com';
    } catch {
      return false;
    }
  };

  const scrollToTop = () => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  };

  return (
    <footer
      className='bg-linear-to-b from-primary to-primary-dark dark:from-gray-800 dark:to-gray-900 text-white py-16 md:py-24 relative overflow-hidden'
      aria-labelledby='footer-heading'
    >
      <h2 id='footer-heading' className='sr-only'>
        Footer
      </h2>

      {/* Background Pattern */}
      <div className='absolute inset-0 opacity-5' aria-hidden={true}>
        <div
          className='absolute inset-0'
          style={{
            backgroundImage:
              'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="1"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            backgroundSize: '30px 30px',
          }}
        />
      </div>

      <div className='container mx-auto px-4 sm:px-6 relative z-10'>
        <div className='grid grid-cols-1 md:grid-cols-3 xl:grid-cols-3 gap-12 xl:gap-8'>
          {/* Contact Info */}
          <div
            className='text-center xl:text-left animate-when-visible'
            style={{ '--animation-duration': '0.6s' } as React.CSSProperties}
            ref={(el) => {
              if (el) {
                const observer = new IntersectionObserver(
                  (entries) => {
                    entries.forEach((entry) => {
                      if (entry.isIntersecting) {
                        entry.target.classList.add('is-visible');
                        observer.unobserve(entry.target);
                      }
                    });
                  },
                  { threshold: 0.1 },
                );
                observer.observe(el);
              }
            }}
          >
            <h3 className='text-2xl font-bold mb-4 font-serif'>{appName}</h3>
            <address className='not-italic text-white/90 mb-6 text-lg'>
              <p className='mb-1'>{facilityStreet}</p>
              <p className='mb-1'>
                {facilityCity}, {facilityState} {facilityZipcode}
              </p>
              <p>License #{facilityLicense}</p>
            </address>

            <div className='mt-6 flex flex-col items-center justify-center xl:items-start xl:justify-start space-y-3 xl:space-t-4'>
              <Link
                href={`https://maps.google.com/?q=${encodeURIComponent(`${facilityStreet}, ${facilityCity}, ${facilityState} ${facilityZipcode}`)}`}
                target='_blank'
                rel='noopener noreferrer'
                className='inline-flex items-center justify-center text-white/90 hover:text-white transition-colors duration-200'
                aria-label='Get directions to our facility'
              >
                <svg className='w-5 h-5 mr-2' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z'
                  />
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M15 11a3 3 0 11-6 0 3 3 0 016 0z'
                  />
                </svg>
                Get Directions
              </Link>

              {isValidInstagramUrl(instagramUrl) && (
                <Link
                  href={instagramUrl}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='inline-flex items-center justify-center text-white/90 hover:text-white transition-colors duration-200'
                  aria-label='Follow us on Instagram'
                >
                  <svg className='w-5 h-5 mr-2' fill='currentColor' viewBox='0 0 24 24'>
                    <path d='M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z' />
                  </svg>
                  Follow Us
                </Link>
              )}
            </div>
          </div>

          {/* Quick Links */}
          <div
            className='text-center animate-when-visible'
            style={
              {
                '--animation-duration': '0.6s',
                '--animation-delay': '0.2s',
              } as React.CSSProperties
            }
            ref={(el) => {
              if (el) {
                const observer = new IntersectionObserver(
                  (entries) => {
                    entries.forEach((entry) => {
                      if (entry.isIntersecting) {
                        entry.target.classList.add('is-visible');
                        observer.unobserve(entry.target);
                      }
                    });
                  },
                  { threshold: 0.1 },
                );
                observer.observe(el);
              }
            }}
          >
            <h3 className='text-2xl font-bold mb-4 font-serif'>Schedule a Visit</h3>
            <a
              href={`tel:${formattedPhone}`}
              className='group inline-flex items-center justify-center px-6 py-3 border border-transparent rounded-lg text-lg font-medium text-primary dark:text-primary-light bg-white dark:bg-gray-900 hover:bg-accent dark:hover:bg-primary-dark hover:text-black dark:hover:text-white transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1 hover:text-shadow-lg hover:text-shadow-yellow-200/20 dark:hover:text-shadow-gray-800/20 *:hover:drop-shadow-lg *:hover:drop-shadow-yellow-200/20 *:dark:hover:drop-shadow-gray-800/20'
              aria-label={`Call us at ${facilityTelephone}`}
            >
              <svg
                className='w-5 h-5 mr-2 group-hover:animate-bounce'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z'
                />
              </svg>
              {facilityTelephone}
            </a>

            <div className='mt-8'>
              <h4 className='text-xl font-semibold mb-4 font-serif'>Quick Links</h4>
              <nav className='flex flex-col space-y-2' aria-label='Footer navigation'>
                <a href='#about' className='text-white/90 hover:text-white transition-colors duration-200'>
                  About Us
                </a>
                <a
                  href='#services-and-ammenities'
                  className='text-white/90 hover:text-white transition-colors duration-200'
                >
                  Services & Amenities
                </a>
                <a href='#photo-gallery' className='text-white/90 hover:text-white transition-colors duration-200'>
                  Photo Gallery
                </a>
                <a href='#waiver' className='text-white/90 hover:text-white transition-colors duration-200'>
                  ALW Program
                </a>
                <a href='#contact-us' className='text-white/90 hover:text-white transition-colors duration-200'>
                  Contact Us
                </a>
              </nav>
            </div>
          </div>

          {/* Logo & Copyright */}
          <div
            className='flex flex-col items-center lg:items-end animate-when-visible'
            style={
              {
                '--animation-duration': '0.6s',
                '--animation-delay': '0.4s',
              } as React.CSSProperties
            }
            ref={(el) => {
              if (el) {
                const observer = new IntersectionObserver(
                  (entries) => {
                    entries.forEach((entry) => {
                      if (entry.isIntersecting) {
                        entry.target.classList.add('is-visible');
                        observer.unobserve(entry.target);
                      }
                    });
                  },
                  { threshold: 0.1 },
                );
                observer.observe(el);
              }
            }}
          >
            <div className='flex w-full lg:w-2/3 h-auto mb-6 align-center justify-center'>
              <LogoSvg colorMode='all-light' />
            </div>

            <p className='text-center lg:text-end text-lg text-white/90 mb-4'>{shortDescription}</p>

            <div className='text-center lg:text-end text-sm text-white/80 mt-6'>
              &copy; {currYear} {appLegalName}
              <br />
              All rights reserved.
            </div>
          </div>
        </div>

        {/* Bottom Links */}
        <div
          className='mt-12 pt-8 border-t border-white/10 text-center text-sm text-white/80 font-serif animate-when-visible'
          style={
            {
              '--animation-duration': '0.6s',
              '--animation-delay': '0.6s',
            } as React.CSSProperties
          }
          ref={(el) => {
            if (el) {
              const observer = new IntersectionObserver(
                (entries) => {
                  entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                      entry.target.classList.add('is-visible');
                      observer.unobserve(entry.target);
                    }
                  });
                },
                { threshold: 0.1 },
              );
              observer.observe(el);
            }
          }}
        >
          <div className='flex flex-col md:flex-row justify-center items-center space-y-4 md:space-y-0'>
            <Link href='/privacy-policy' className='hover:text-white transition-colors duration-200 mx-3'>
              Privacy Policy
            </Link>
            <span className='hidden md:inline mx-3'>•</span>
            <Link href='/terms-of-use' className='hover:text-white transition-colors duration-200 mx-3'>
              Terms of Use
            </Link>
            <span className='hidden md:inline mx-3'>•</span>
            <p className='mx-3'>
              <button
                type='button'
                onClick={scrollToTop}
                className='hover:text-white transition-colors duration-200'
                aria-label='Back to top'
              >
                Back to Top
              </button>
            </p>
            <span className='hidden md:inline mx-3'>•</span>
            <ThemeSwitcherWrapper />
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
